import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Layout } from 'lucide-react';

interface SaasDetails {
  id: string;
  company_name: string;
  website: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  postal_code: string;
  city: string;
  created_at: string;
  updated_at: string;
}

export function SaasDetails() {
  const [details, setDetails] = useState<SaasDetails | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<SaasDetails>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSaasDetails();
  }, []);

  const loadSaasDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const { data, error: queryError } = await supabase
        .from('saas_details')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (queryError && queryError.code !== 'PGRST116') {
        throw queryError;
      }

      setDetails(data);
      setFormData(data || {});
    } catch (err) {
      setError('Failed to load SaaS details');
      console.error('Error loading details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Validate required fields
      const requiredFields = ['company_name', 'website', 'contact_email', 'contact_phone', 'address', 'postal_code', 'city'] as const;
      for (const field of requiredFields) {
        if (!formData[field]?.trim()) {
          setError(`${field.replace('_', ' ').toUpperCase()} is required`);
          return;
        }
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact_email || '')) {
        setError('Please enter a valid email address');
        return;
      }

      // Validate website URL
      try {
        new URL(formData.website || '');
      } catch {
        setError('Please enter a valid website URL');
        return;
      }

      const { error: upsertError } = await supabase
        .from('saas_details')
        .upsert({
          id: user.id,
          ...formData,
          updated_at: new Date().toISOString(),
        });

      if (upsertError) throw upsertError;

      setIsEditing(false);
      await loadSaasDetails();
    } catch (err) {
      setError('Failed to save SaaS details');
      console.error('Error saving details:', err);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">Loading SaaS details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Layout className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-700">SaaS Details</h2>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary"
            >
              Edit Details
            </button>
          )}
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Company Name</label>
              <input
                type="text"
                value={formData.company_name || ''}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Address</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Postal Code</label>
                <input
                  type="text"
                  value={formData.postal_code || ''}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">City</label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">Website</label>
              <input
                type="url"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="form-input"
                placeholder="https://example.com"
                required
              />
            </div>

            <div>
              <label className="form-label">Contact Email</label>
              <input
                type="email"
                value={formData.contact_email || ''}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Contact Phone</label>
              <input
                type="tel"
                value={formData.contact_phone || ''}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="form-input"
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setError('');
                  setFormData(details || {});
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </form>
        ) : details ? (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Company Name</h3>
              <p className="mt-1 text-sm text-gray-900">{details.company_name}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Address</h3>
              <p className="mt-1 text-sm text-gray-900">{details.address}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Postal Code</h3>
                <p className="mt-1 text-sm text-gray-900">{details.postal_code}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">City</h3>
                <p className="mt-1 text-sm text-gray-900">{details.city}</p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Website</h3>
              <p className="mt-1 text-sm">
                <a
                  href={details.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600"
                >
                  {details.website}
                </a>
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Contact Email</h3>
              <p className="mt-1 text-sm text-gray-900">{details.contact_email}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Contact Phone</h3>
              <p className="mt-1 text-sm text-gray-900">{details.contact_phone}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="px-6 py-4 text-center text-sm text-gray-500">
              No SaaS details configured yet. Add your company information to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}